import { CalendarPlus, Gift, Percent, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { FALLBACK_CATALOG, planKeySchema, type PlanKey } from '@/lib/plan-catalog';

import type { BusinessSubscription } from './admin-schemas';
import { useBillingAction, useBusinessBilling, type BillingAction } from './billing-api';
import {
  CouponValue,
  SeatsValue,
  SubscriptionDate,
  SubscriptionStatusBadge,
} from './billing-report';
import { ConfirmActionDialog } from './confirm-action-dialog';
import { money, planLabel } from './labels';

type Kind = BillingAction['kind'];

const israelDay = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(d);

/** מושבים נוספים מותרים לתוכנית (כמו בשרת: עד תקרת המושבים הכוללת). */
function maxExtraSeats(plan: PlanKey): number {
  const p = FALLBACK_CATALOG.plans.find((x) => x.key === plan);
  return p?.extraSeatsAllowed ? Math.max(0, FALLBACK_CATALOG.maxTotalSeats - p.seats) : 0;
}

/** מספר שלם בטווח, או null. */
function intIn(raw: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const v = Number(raw.trim());
  return v >= min && v <= max ? v : null;
}

/**
 * המנוי במגירת העסק: מצב, תוכנית, ניסיון / חידוש, מושבים, קופון ותוכנית חינם —
 * ופעולות המפעיל (הארכת ניסיון, קופון, תוכנית חינם), כל אחת דרך חלון אישור.
 */
export function BusinessBilling({
  businessId,
  businessName,
}: {
  businessId: string;
  businessName: string;
}) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const q = useBusinessBilling(businessId);
  const action = useBillingAction();
  const [open, setOpen] = useState<Kind | null>(null);

  const run = (a: BillingAction) =>
    action.mutate(
      { id: businessId, action: a },
      {
        onSuccess: () => {
          setOpen(null);
          toast.success(t(`billing.done.${a.kind}`, { name: businessName }));
        },
        onError: (err) =>
          toast.error(t('billing.failed'), err instanceof ApiError ? err.message : undefined),
      },
    );

  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton className="h-24 w-full" />;
  const sub = q.data.subscription;

  // השרת מאריך ניסיון רק לעסק שעוד לא שילם (בניסיון, או שהניסיון פג בלי תשלום).
  const canExtend =
    !sub ||
    sub.status === 'TRIALING' ||
    (sub.status === 'EXPIRED' && sub.paidInvoices === 0 && !sub.complimentary);

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold text-fg">{t('billing.drawer.title')}</h3>
      {sub ? (
        <SubscriptionDetails sub={sub} />
      ) : (
        <p className="text-xs text-fg-muted">{t('billing.drawer.none')}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {canExtend ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen('extend-trial')}>
            <CalendarPlus aria-hidden />
            {t('billing.actions.extend-trial')}
          </Button>
        ) : null}
        {sub ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen('coupon')}>
            <Percent aria-hidden />
            {t('billing.actions.coupon')}
          </Button>
        ) : null}
        {sub?.coupon ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen('remove-coupon')}>
            <XCircle aria-hidden />
            {t('billing.actions.remove-coupon')}
          </Button>
        ) : null}
        <Button size="sm" variant="secondary" onClick={() => setOpen('complimentary')}>
          <Gift aria-hidden />
          {t('billing.actions.complimentary')}
        </Button>
      </div>

      {open === 'extend-trial' ? (
        <ExtendTrialDialog
          name={businessName}
          loading={action.isPending}
          onClose={() => setOpen(null)}
          onConfirm={(days) => run({ kind: 'extend-trial', days })}
        />
      ) : null}
      {open === 'coupon' ? (
        <CouponDialog
          name={businessName}
          loading={action.isPending}
          onClose={() => setOpen(null)}
          onConfirm={(percent, months) => run({ kind: 'coupon', percent, months })}
        />
      ) : null}
      {open === 'remove-coupon' ? (
        <ConfirmActionDialog
          title={t('billing.confirm.remove-coupon.title', { name: businessName })}
          description={t('billing.confirm.remove-coupon.description')}
          confirmLabel={t('billing.actions.remove-coupon')}
          danger
          loading={action.isPending}
          onClose={() => setOpen(null)}
          onConfirm={() => run({ kind: 'remove-coupon' })}
        />
      ) : null}
      {open === 'complimentary' ? (
        <ComplimentaryDialog
          name={businessName}
          initialPlan={sub?.plan ?? 'TEAM'}
          loading={action.isPending}
          onClose={() => setOpen(null)}
          onConfirm={(a) => run(a)}
        />
      ) : null}
    </section>
  );
}

function SubscriptionDetails({ sub }: { sub: BusinessSubscription }) {
  const { t } = useTranslation('admin');
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <SubscriptionStatusBadge sub={sub} />
        {sub.readOnly ? (
          <span className="text-2xs text-danger">{t('billing.readOnly')}</span>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label={t('billing.table.columns.plan')}>
          {planLabel(sub.plan)} · {t(`billing.interval.${sub.interval}`)}
        </Stat>
        <Stat label={t('billing.table.columns.dates')}>
          <SubscriptionDate sub={sub} />
        </Stat>
        {sub.status === 'TRIALING' && sub.trialDaysLeft !== null ? (
          <Stat label={t('billing.drawer.trialDaysLeft')}>{sub.trialDaysLeft}</Stat>
        ) : null}
        <Stat label={t('billing.table.columns.seats')}>
          <SeatsValue sub={sub} />
        </Stat>
        <Stat label={t('billing.table.columns.mrr')}>
          <bdi>{money(sub.mrrMinor)}</bdi>
        </Stat>
        <Stat label={t('billing.table.columns.coupon')}>
          <CouponValue sub={sub} />
        </Stat>
        <Stat label={t('billing.table.columns.complimentary')}>
          {sub.complimentary ? t('billing.yes') : '—'}
        </Stat>
        {sub.pendingPlan ? (
          <Stat label={t('billing.drawer.pending')}>{planLabel(sub.pendingPlan)}</Stat>
        ) : null}
      </dl>
    </>
  );
}

function ExtendTrialDialog({
  name,
  loading,
  onClose,
  onConfirm,
}: {
  name: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (days: number) => void;
}) {
  const { t } = useTranslation('admin');
  const [days, setDays] = useState('7');
  const [error, setError] = useState<string | undefined>();
  return (
    <ConfirmActionDialog
      title={t('billing.confirm.extend-trial.title', { name })}
      description={t('billing.confirm.extend-trial.description')}
      confirmLabel={t('billing.actions.extend-trial')}
      loading={loading}
      onClose={onClose}
      onConfirm={() => {
        const v = intIn(days, 1, 90);
        if (v === null) return setError(t('billing.errors.days'));
        setError(undefined);
        onConfirm(v);
      }}
    >
      <Field label={t('billing.form.days')} htmlFor="billing-days" error={error}>
        <Input
          id="billing-days"
          type="number"
          inputMode="numeric"
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
      </Field>
    </ConfirmActionDialog>
  );
}

function CouponDialog({
  name,
  loading,
  onClose,
  onConfirm,
}: {
  name: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (percent: number, months: number) => void;
}) {
  const { t } = useTranslation('admin');
  const [percent, setPercent] = useState('');
  const [months, setMonths] = useState('3');
  const [errors, setErrors] = useState<{ percent?: string; months?: string }>({});
  return (
    <ConfirmActionDialog
      title={t('billing.confirm.coupon.title', { name })}
      description={t('billing.confirm.coupon.description')}
      confirmLabel={t('billing.actions.coupon')}
      loading={loading}
      onClose={onClose}
      onConfirm={() => {
        const p = intIn(percent, 1, 100);
        const m = intIn(months, 1, 36);
        const next = {
          percent: p === null ? t('billing.errors.percent') : undefined,
          months: m === null ? t('billing.errors.months') : undefined,
        };
        setErrors(next);
        if (p !== null && m !== null) onConfirm(p, m);
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('billing.form.percent')} htmlFor="billing-percent" error={errors.percent}>
          <Input
            id="billing-percent"
            type="number"
            inputMode="numeric"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </Field>
        <Field label={t('billing.form.months')} htmlFor="billing-months" error={errors.months}>
          <Input
            id="billing-months"
            type="number"
            inputMode="numeric"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
          />
        </Field>
      </div>
    </ConfirmActionDialog>
  );
}

function ComplimentaryDialog({
  name,
  initialPlan,
  loading,
  onClose,
  onConfirm,
}: {
  name: string;
  initialPlan: PlanKey;
  loading: boolean;
  onClose: () => void;
  onConfirm: (a: Extract<BillingAction, { kind: 'complimentary' }>) => void;
}) {
  const { t } = useTranslation('admin');
  const [plan, setPlan] = useState<PlanKey>(initialPlan);
  const [until, setUntil] = useState('');
  const [seats, setSeats] = useState('0');
  const [errors, setErrors] = useState<{ until?: string; seats?: string }>({});
  const tomorrow = israelDay(new Date(Date.now() + 86_400_000));
  const maxSeats = maxExtraSeats(plan);

  return (
    <ConfirmActionDialog
      title={t('billing.confirm.complimentary.title', { name })}
      description={t('billing.confirm.complimentary.description')}
      confirmLabel={t('billing.actions.complimentary')}
      loading={loading}
      onClose={onClose}
      onConfirm={() => {
        const extra = maxSeats > 0 ? intIn(seats || '0', 0, maxSeats) : 0;
        const badUntil = until !== '' && until < tomorrow;
        setErrors({
          until: badUntil ? t('billing.errors.until') : undefined,
          seats: extra === null ? t('billing.errors.seats', { max: maxSeats }) : undefined,
        });
        if (badUntil || extra === null) return;
        onConfirm({
          kind: 'complimentary',
          plan,
          ...(until ? { until } : {}),
          ...(maxSeats > 0 ? { extraSeats: extra } : {}),
        });
      }}
    >
      <div className="space-y-3">
        <Field label={t('billing.form.plan')} htmlFor="billing-plan">
          <Select value={plan} onValueChange={(v) => setPlan(planKeySchema.parse(v))}>
            <SelectTrigger id="billing-plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {planKeySchema.options.map((k) => (
                <SelectItem key={k} value={k}>
                  {planLabel(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field
          label={t('billing.form.until')}
          htmlFor="billing-until"
          hint={t('billing.form.untilHint')}
          error={errors.until}
        >
          <Input
            id="billing-until"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
          />
        </Field>
        {maxSeats > 0 ? (
          <Field
            label={t('billing.form.extraSeats')}
            htmlFor="billing-seats"
            hint={t('billing.form.extraSeatsHint', { max: maxSeats })}
            error={errors.seats}
          >
            <Input
              id="billing-seats"
              type="number"
              inputMode="numeric"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
            />
          </Field>
        ) : null}
      </div>
    </ConfirmActionDialog>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 px-3 py-2">
      <dt className="text-2xs text-fg-muted">{label}</dt>
      <dd className="tabular text-sm font-semibold text-fg">{children}</dd>
    </div>
  );
}
